// Simple API client wrapper for consistent error handling and auth

class ApiClient {
  // Callers pass relative paths ("/api/..."). Locally the Vite proxy would make
  // that work, but on prod the frontend is a static site on a DIFFERENT host than
  // the backend — a relative fetch hits the static site (SPA index.html) and never
  // reaches the API. Prefix with VITE_API_URL like every legacy call site does.
  private baseUrl: string = import.meta.env.VITE_API_URL || "";

  async request(url: string, options: RequestInit = {}) {
    const token = localStorage.getItem("auth_token");
    
    const headers = {
      ...options.headers,
      Authorization: token ? `Bearer ${token}` : "",
    };

    const fullUrl = url.startsWith("/") ? `${this.baseUrl}${url}` : url;

    const response = await fetch(fullUrl, {
      ...options,
      headers,
    });

    // Global handling: an expired/invalidated session (401) clears the token and
    // signals AuthContext to drop to the login screen. (Covers calls made through
    // this client; raw fetch callers still handle their own errors.)
    if (response.status === 401) {
      localStorage.removeItem("auth_token");
      window.dispatchEvent(new Event("auth:unauthorized"));
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || error.error || `Request failed: ${response.status}`);
    }

    return response;
  }

  async get(url: string) {
    const response = await this.request(url);
    return response.json();
  }

  async post(url: string, data?: any) {
    const response = await this.request(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: data ? JSON.stringify(data) : undefined,
    });
    return response.json();
  }

  async put(url: string, data?: any) {
    const response = await this.request(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: data ? JSON.stringify(data) : undefined,
    });
    return response.json();
  }

  async patch(url: string, data?: any) {
    const response = await this.request(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: data ? JSON.stringify(data) : undefined,
    });
    return response.json();
  }

  async delete(url: string) {
    const response = await this.request(url, {
      method: "DELETE",
    });
    return response.json().catch(() => ({ success: true }));
  }
}

export const apiClient = new ApiClient();
